// Founding developers (docs/build/founding-devs.md): the invite flow's pure parts. It holds:
// - the answer option lists, with their slugs mirrored from backend/accounts/src/dev-apply-options.ts;
// - the draft kept in this browser until Send;
// - address matching against the catalogue;
// - the words the applicant sees.
// The pages are pages/apply.ts (the flow), dev-mod.ts (the moderators' Developers tab and invite maker) and
// pages/dash-application.ts (the dashboard panel). The shapes are accounts' as built: docs/backend/accounts.md
// "Founding developers". test/dev-apply-fake.ts answers with them until the backend merges.
//
// The internal score is for moderators only: nothing here that the applicant sees mentions it.

import type { Catalogue, Game } from "./catalogue.ts";

// ---- the answers (screens 3–6): slugs mirror accounts' DEV_APPLY ------------------------------------------------

export const APPLY_OPTIONS = {
  /** screen 3, 1 or more */
  discovery: [
    ["x_posts", "X posts"],
    ["portals", "Portals: itch, Poki, CrazyGames"],
    ["reddit_discord", "Reddit or Discord"],
    ["own_site", "My own site or newsletter"],
    ["mostly_dont", "They mostly don't"],
  ],
  /** screen 4, with one line on what they're working on */
  when: [
    ["this_month", "This month"],
    ["this_year", "This year"],
    ["exploring", "Just exploring"],
  ],
  /** screen 5, exactly 1 */
  money: [
    ["not_yet", "Not yet"],
    ["ads", "Ads"],
    ["sponsorship_client", "Sponsorship or client work"],
    ["sales_tips", "Sales or tips"],
  ],
  /** screen 6 */
  studio: [
    ["solo", "Solo"],
    ["studio", "A studio or team"],
  ],
} as const satisfies Record<string, readonly (readonly [string, string])[]>;

/** accounts' DEV_APPLY_LIMITS, the ones the flow enforces */
export const LIMITS = { displayName: 60, nextWhat: 200, studioName: 80, creatorIds: 20, urls: 10 } as const;
export const MAX_URLS = LIMITS.urls;

type Slug<K extends keyof typeof APPLY_OPTIONS> = (typeof APPLY_OPTIONS)[K][number][0];
export type Discovery = Slug<"discovery">;
export type When = Slug<"when">;
export type Money = Slug<"money">;
export type Studio = Slug<"studio">;

export const optionLabel = (k: keyof typeof APPLY_OPTIONS, slug: string): string =>
  (APPLY_OPTIONS[k] as readonly (readonly [string, string])[]).find(([s]) => s === slug)?.[1] ?? slug;

export interface Answers {
  discovery: Discovery[];
  next: { what: string; when: When | null };
  money: Money | null;
}

export interface About {
  displayName: string;
  xHandle: string;
  site: string;
  studio: Studio | null;
}

// ---- what accounts answers ---------------------------------------------------------------------------------------

export interface Cohort {
  capacity: number;
  applied: number;
  approved: number;
  left: number;
}

/** A game as the invite and the application carry it. */
export interface Tile {
  id: string;
  title: string;
  url?: string;
  art: string | null;
  artKind?: string | null;
  cover?: string | null;
}

/** GET /v1/dev-invites/:code */
export interface InviteView {
  /** invitedBy: the inviting moderator's display name (or handle); null for an invite made by a service */
  invite: { code: string; name: string; xHandle: string; expiresAt: number | null; used: boolean; invitedBy?: string | null };
  /** the live count (accounts ce2ade9d); older builds leave it out */
  cohort?: Cohort;
  creators: { id: string; name: string; why: "invite" | "creator_listing"; claimable: boolean; games: Tile[] }[];
}

export type Status = "submitted" | "approved" | "waitlisted" | "declined";
export type CreatorState = "pending" | "yours" | "claimed_elsewhere" | "approved" | "rejected" | "withdrawn" | "none";
export type UrlState = "listed" | "being_added";

export interface Application {
  id: string;
  status: Status;
  inviteCode: string | null;
  displayName: string;
  xHandle: string | null;
  site: string | null;
  studio: Studio;
  studioName?: string | null;
  answers: { discovery: Discovery[]; next: { what: string; when: When }; money: Money };
  creators: { id: string; name: string; state: CreatorState; claim: { id: string; status: string } | null; games: Tile[]; claimedBy?: string | null }[];
  urls: { url: string; host: string; listed: { gameId: string; creatorId: string; title: string } | null; state: UrlState }[];
  note: string | null;
  perks?: string[] | null;
  decidedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/** GET and PUT /v1/dev-applications/me: `application` is null before the first PUT. */
export interface MyApplication {
  application: Application | null;
  inventory: { listed: number; beingAdded: number };
  cohort: Cohort;
}

/** The PUT /v1/dev-applications/me body. */
export interface Submission {
  inviteCode: string | null;
  displayName: string;
  xHandle?: string;
  site?: string;
  studio: Studio | null;
  answers: Answers;
  creatorIds: string[];
  urls: string[];
}

// ---- the flow's screens ------------------------------------------------------------------------------------------

/** 0 hello · 1 yours · 2 other addresses · 3 discovery · 4 next · 5 money · 6 about you · 7 save · 8 result */
export const LAST_QUESTION = 6;
export const SAVE = 7;
export const RESULT = 8;

/** The disclosure on screen 6 (founding-devs.md "Enrichment"; the owner is in the EU, so it's said up front). */
export const DISCLOSURE = "We'll look at your public profile and your game pages to review this.";

/** The invite's games as tiles, each with its creator (claims are per creator). */
export interface InviteGame extends Tile {
  creatorId: string;
}

export function inviteGames(v: Pick<InviteView, "creators">): InviteGame[] {
  return v.creators.flatMap((c) => c.games.map((g) => ({ ...g, creatorId: c.id })));
}

// ---- the draft: screens 1–6 stay in this browser until Send -----------------------------------------------------

export interface Draft {
  v: 2;
  code: string;
  step: number;
  creatorIds: string[];
  /** games the person found in the catalogue search (their creators are in creatorIds), shown as tiles */
  foundGameIds: string[];
  urlsText: string;
  answers: Answers;
  about: About;
  /** the last screen they've reached, so a reload or the sign-in round trip comes back there */
  reached: number;
}

const DRAFT_KEY = "thrdm.apply";

export function emptyDraft(code: string, view?: InviteView | null): Draft {
  return {
    v: 2,
    code,
    step: 0,
    // offered creators start ticked when they can be claimed (rules 1 and 2; one someone else holds can't)
    creatorIds: view?.creators.filter((c) => c.claimable).map((c) => c.id) ?? [],
    foundGameIds: [],
    urlsText: "",
    answers: { discovery: [], next: { what: "", when: null }, money: null },
    about: { displayName: (view?.invite.name ?? "").replace(/^@/, ""), xHandle: view?.invite.xHandle ?? "", site: "", studio: null },
    reached: 0,
  };
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const local = (): Store | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

/** The saved draft for this invite, or null (none, another invite's, an older shape, unreadable, storage blocked). */
export function loadDraft(code: string, store: Store | null = local()): Draft | null {
  try {
    const d = JSON.parse(store?.getItem(DRAFT_KEY) ?? "null") as Draft | null;
    if (!d || d.v !== 2 || d.code !== code) return null;
    const e = emptyDraft(code);
    return { ...e, ...d, answers: { ...e.answers, ...d.answers, next: { ...e.answers.next, ...d.answers?.next } }, about: { ...e.about, ...d.about } };
  } catch {
    return null;
  }
}

/** Saves the draft; without storage (private mode, blocked) the flow still works in this tab. */
export function saveDraft(d: Draft, store: Store | null = local()): void {
  try {
    store?.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* the draft lasts this page only */
  }
}

export function clearDraft(store: Store | null = local()): void {
  try {
    store?.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** A draft from what they sent, for "Change your answers" (answers are editable until decided). */
export function draftFrom(code: string, a: Application, view?: InviteView | null): Draft {
  const d = emptyDraft(code, view);
  d.creatorIds = a.creators.filter((c) => c.state !== "withdrawn" && c.state !== "claimed_elsewhere").map((c) => c.id);
  d.urlsText = a.urls.filter((u) => u.state === "being_added").map((u) => u.url).join("\n");
  d.answers = { discovery: a.answers.discovery, next: { ...a.answers.next }, money: a.answers.money };
  d.about = { displayName: a.displayName, xHandle: a.xHandle ?? "", site: a.site ?? "", studio: a.studio };
  d.reached = SAVE;
  return d;
}

// ---- addresses (screen 2): matched against the catalogue only, nothing is fetched --------------------------------

/**
 * The launcher's library key for a game address (a copy of backend/accounts/src/url-key.ts `urlTarget`, which the
 * site can't import): host without `www.` and port, path without a trailing slash, the query. Null when it isn't an
 * http(s) address. A bare `example.com/game` is read as https.
 */
export function urlKey(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  let u: URL;
  try {
    u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`);
  } catch {
    return null;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  if (!u.hostname.includes(".")) return null;
  const host = u.hostname.replace(/^(www\.)+/, "");
  const path = u.pathname.replace(/\/+$/, "");
  return `url:${host}${path}${u.search}`;
}

/** The address as a person reads it: host without www, plus the path when there is one. */
export function domainOf(raw: string): string {
  const k = urlKey(raw);
  return k ? k.slice(4) : raw.trim();
}

/** The address accounts can read (it refuses anything that isn't http(s)): a bare `example.com/game` gets https. */
export function fullUrl(raw: string): string {
  const t = raw.trim();
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

export interface UrlLine {
  raw: string;
  key: string | null;
  /** on Banger already */
  game: (Pick<Game, "id" | "title" | "art"> & { creatorId: string; creatorName: string }) | null;
  problem: "not_an_address" | "duplicate" | null;
}

/** Each pasted line (blank lines dropped, at most MAX_URLS kept), checked against the catalogue by url key. */
export function matchUrls(text: string, games: Pick<Game, "id" | "title" | "art" | "url" | "creator">[]): { lines: UrlLine[]; over: number } {
  const byKey = new Map<string, (typeof games)[number]>();
  for (const g of games) {
    const k = urlKey(g.url);
    if (k && !byKey.has(k)) byKey.set(k, g);
  }
  const raws = text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const lines = raws.slice(0, MAX_URLS).map((raw): UrlLine => {
    const key = urlKey(raw);
    if (!key) return { raw, key, game: null, problem: "not_an_address" };
    if (seen.has(key)) return { raw, key, game: null, problem: "duplicate" };
    seen.add(key);
    const g = byKey.get(key);
    return { raw, key, game: g ? { id: g.id, title: g.title, art: g.art, creatorId: g.creator.id, creatorName: g.creator.name } : null, problem: null };
  });
  return { lines, over: Math.max(0, raws.length - MAX_URLS) };
}

/** The addresses that go to accounts as new: valid, not repeated, not already on Banger. */
export const newUrls = (lines: UrlLine[]): string[] => lines.filter((l) => !l.problem && !l.game).map((l) => fullUrl(l.raw));

// ---- "these are yours" (screen 1): claims are per creator ---------------------------------------------------------

/** Ticks or unticks a creator (every game of theirs moves with it: a claim is for the creator listing). */
export function toggleCreator(ids: string[], creatorId: string): string[] {
  return ids.includes(creatorId) ? ids.filter((i) => i !== creatorId) : [...ids, creatorId];
}

/** Catalogue search for "Something missing?": title or creator name, best first, unclaimed listings only. */
export function searchGames(games: Game[], q: string, limit = 6): Game[] {
  const n = q.trim().toLowerCase();
  if (n.length < 2) return [];
  const score = (g: Game) => {
    const t = g.title.toLowerCase();
    const c = g.creator.name.toLowerCase();
    return t === n ? 0 : t.startsWith(n) ? 1 : c.startsWith(n) ? 2 : t.includes(n) ? 3 : c.includes(n) ? 4 : 9;
  };
  return games
    .filter((g) => !g.creator.claimedBy && !g.firstParty && score(g) < 9)
    .sort((a, b) => score(a) - score(b) || a.title.localeCompare(b.title))
    .slice(0, limit);
}

/** The tiles on screen 1: the invite's games, then the games found by search (each once). */
export function yoursTiles(invited: InviteGame[], found: InviteGame[]): InviteGame[] {
  const out: InviteGame[] = [];
  const ids = new Set<string>();
  for (const g of [...invited, ...found]) if (!ids.has(g.id)) (ids.add(g.id), out.push(g));
  return out;
}

export const asInviteGame = (g: Pick<Game, "id" | "title" | "art" | "creator">): InviteGame => ({ id: g.id, title: g.title, art: g.art, creatorId: g.creator.id });

// ---- about you (screen 6) ----------------------------------------------------------------------------------------

/** "@name", "x.com/name", "https://twitter.com/name" → "name"; null when it isn't an X handle. */
export function readHandle(v: string): string | null {
  const t = v.trim().replace(/^https?:\/\//i, "").replace(/^(www\.)?(x|twitter)\.com\//i, "").replace(/^@/, "").replace(/[/?#].*$/, "");
  return /^[A-Za-z0-9_]{1,15}$/.test(t) ? t : null;
}

/** What's wrong with screen 6, in plain words, or null. */
export function aboutProblem(a: About): string | null {
  if (!a.displayName.trim()) return "Tell us the name you make games under.";
  if (a.displayName.trim().length > LIMITS.displayName) return `That name is a bit long: ${LIMITS.displayName} characters at most.`;
  if (a.xHandle.trim() && !readHandle(a.xHandle)) return "That X handle doesn't look right. It's the part after x.com/.";
  if (a.site.trim() && !urlKey(a.site)) return "That site doesn't look like a web address.";
  if (!a.studio) return "Solo, or a studio?";
  return null;
}

/** Which question screens are answered (the Continue button waits on these; 1 and 2 are optional). */
export function answered(d: Draft, step: number): boolean {
  if (step === 3) return d.answers.discovery.length > 0;
  if (step === 4) return d.answers.next.when !== null && d.answers.next.what.trim().length > 0;
  if (step === 5) return d.answers.money !== null;
  if (step === 6) return aboutProblem(d.about) === null;
  return true;
}

/** The furthest screen a draft may open on: no skipping past an unanswered question. */
export function furthest(d: Draft): number {
  for (let s = 1; s <= LAST_QUESTION; s++) if (!answered(d, s)) return s;
  return SAVE;
}

export function submission(d: Draft, cat: Pick<Catalogue, "games">): Submission {
  const { lines } = matchUrls(d.urlsText, cat.games);
  // accounts matches pasted addresses itself; listed ones are sent as their creators, so the tiles and the server agree
  const listedCreators = lines.flatMap((l) => (l.game ? [l.game.creatorId] : []));
  const x = readHandle(d.about.xHandle);
  const site = d.about.site.trim();
  return {
    inviteCode: d.code || null,
    displayName: d.about.displayName.trim(),
    ...(x ? { xHandle: x } : {}),
    ...(site ? { site: fullUrl(site) } : {}),
    studio: d.about.studio,
    answers: { ...d.answers, next: { what: d.answers.next.what.trim().slice(0, LIMITS.nextWhat), when: d.answers.next.when } },
    creatorIds: [...new Set([...d.creatorIds, ...listedCreators])].slice(0, LIMITS.creatorIds),
    urls: newUrls(lines),
  };
}

// ---- what the applicant sees after Send (screen 8, the dashboard) -------------------------------------------------

/** "4 of your games are on Banger already; 2 more are being added." */
export function inventoryLine(listed: number, adding: number): string {
  const games = (n: number) => `${n} ${n === 1 ? "game" : "games"}`;
  const on = listed === 0 ? "" : listed === 1 ? "1 of your games is on Banger already" : `${listed} of your games are on Banger already`;
  const add = adding === 0 ? "" : listed === 0 ? `${games(adding)} ${adding === 1 ? "is" : "are"} being added` : `${adding} more ${adding === 1 ? "is" : "are"} being added`;
  if (on && add) return `${on}; ${add}.`;
  if (on || add) return `${on || add}.`;
  return "No games yet. Add one any time from your dashboard.";
}

/** The application's games that are, or will be, theirs (not ones someone else holds or that they dropped). */
export function applicationGames(a: Application): InviteGame[] {
  return a.creators.filter((c) => c.state !== "claimed_elsewhere" && c.state !== "withdrawn" && c.state !== "rejected").flatMap((c) => c.games.map((g) => ({ ...g, creatorId: c.id })));
}

/**
 * One line per answer from screens 3–5 that maps to something real on Banger, each a line from the claims ledger
 * (docs/launch/claims.md; the id is beside each). Nothing about the score, perks or earnings promises.
 */
export function answerLines(a: Pick<Answers, "discovery" | "next" | "money">): { id: string; text: string }[] {
  const out: { id: string; text: string }[] = [];
  // P2 (backed) and P1 (H: "when your game updates", never "instantly")
  if (a.discovery.length) out.push({ id: "P1", text: "Players open your game in one click from their library, and hear about it when your game updates." });
  // H8 (H: "that passes review"; creators are 18+) for this month, else P6 (backed)
  if (a.next.when === "this_month") out.push({ id: "H8", text: "Everyone 18+ who ships something spooky that passes review is in the Halloween row." });
  else if (a.next.when) out.push({ id: "P6", text: "When it's close, you can build a waitlist before launch." });
  // P10 (backed) and P9 (not at launch: "coming", no date, "of net", never "you'll earn")
  if (a.money) out.push({ id: "P9", text: "Selling is off at launch. When it comes, creators will keep 80% of net." });
  return out;
}

/**
 * The day they'll hear by: reviews run in daily batches (decision 3), so two days after they applied leaves room for
 * a batch that's already been done. Accounts sends no date yet.
 */
export const replyBy = (createdAt: number): number => createdAt + 2 * 86_400;

/** "Wednesday 21 October" (the day only). */
export function dayText(unixSeconds: number, timeZone = "UTC"): string {
  return new Date(unixSeconds * 1000).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone });
}

/** "The 50 founding developers close on" (decision 3: Sat 31 Oct, Plaza night). */
export const FOUNDING_BY = "2026-10-31";

/** "12 applied · 47 of 50 places left", or null without the numbers. */
export function foundingLine(c: Cohort | undefined | null): string | null {
  if (!c || !Number.isFinite(c.capacity) || !Number.isFinite(c.left)) return null;
  const left = Math.max(0, c.left);
  const places = `${left} of ${c.capacity} ${c.capacity === 1 ? "place" : "places"} left`;
  // no "0 applied": an empty room isn't the signal
  return c.applied > 0 ? `${c.applied} applied · ${places}` : places;
}

export const STATUS_COPY: Record<Status, { badge: string; title: string; body: string }> = {
  submitted: { badge: "In review", title: "Your application is in", body: "We review in daily batches, by hand." },
  approved: { badge: "Founding developer", title: "You're a founding developer", body: "Your games are in your dashboard now, with their players, notes and community." },
  waitlisted: { badge: "Next round", title: "You're first in line for the next round", body: "Open Haunt is open to anyone 18+ now: ship something spooky and it's in the Halloween row once it passes review." },
  declined: { badge: "Not this round", title: "Not this round", body: "Thank you for showing us your games. Everything on Banger stays open to you: claim your listings and keep shipping." },
};

export const URL_STATE: Record<UrlState, string> = { being_added: "Being added", listed: "On Banger" };

/** Plain words for the PUT's refusals that aren't the age (that one gets the 18+ note). */
export const PUT_ERRORS: Record<string, string> = {
  invite_used: "This invite has been used by another account. If it was you, sign in with that account.",
  invite_expired: "This invite has expired. Reply to the message it came in, and we'll send you a fresh one.",
  invite_not_found: "This invite link doesn't work any more. Reply to the message it came in.",
  application_decided: "Your application has already been decided, so it can't be changed now.",
  invalid_url: "One of your game addresses isn't a web address. Go back and check them.",
};

// ---- moderators: the score parts in a fixed order ----------------------------------------------------------------

export interface ScorePart {
  points: number;
  max: number;
  why: string[];
}

export const SCORE_PARTS = [
  ["craft", "Craft"],
  ["reach", "Reach"],
  ["momentum", "Momentum"],
  ["fit", "Fit"],
] as const;

export type XEvidence = "invite" | "creator_listing" | "game_page" | "none";
export const EVIDENCE_LABEL: Record<XEvidence, string> = {
  invite: "Invite: the owner messaged this handle",
  creator_listing: "Creator listing keyed by this X profile",
  game_page: "A game page links this handle (unverified)",
  none: "No evidence yet: unverified",
};

export interface ModCard extends Application {
  decidedBy?: string | null;
  user: { id: string; email: string | null; handle: string | null; foundingAt: number | null };
  xEvidence: XEvidence;
  invite: { code: string; name: string; xHandle: string } | null;
  inventory?: { listed: number; beingAdded: number };
  enrichment: {
    catalogue?: { games: { id: string; title: string; creatorId: string; quality?: number | null; artTier?: string | null; firstFrameMs?: number | null; hearts?: number; players?: number }[]; refreshedAt?: number };
    x: { followers: number | null; likes: number | null; by: string; at: number } | null;
    rating: { value: number; by: string; at: number } | null;
  };
  score: { total: number } & Partial<Record<(typeof SCORE_PARTS)[number][0], ScorePart>>;
}

export interface InviteRow {
  code: string;
  url: string;
  name: string;
  xHandle: string;
  creatorIds: string[];
  gameIds: string[];
  note: string | null;
  createdAt: number;
  expiresAt: number | null;
  expired: boolean;
  used: boolean;
  usedBy: string | null;
  usedAt: number | null;
  application: { id: string; status: Status } | null;
}

/** Cards best first (accounts sorts too; this keeps the order after one card's score changes). */
export const byScore = (cards: ModCard[]): ModCard[] => [...cards].sort((a, b) => b.score.total - a.score.total || a.createdAt - b.createdAt);

/** A typed count ("12,400", "12.4k", "") → whole number or null; undefined when it can't be read. */
export function readCount(v: string): number | null | undefined {
  const t = v.trim().toLowerCase().replace(/[,\s_]/g, "");
  if (!t) return null;
  const m = /^(\d+(?:\.\d+)?)(k|m)?$/.exec(t);
  if (!m) return undefined;
  return Math.round(parseFloat(m[1]!) * (m[2] === "k" ? 1e3 : m[2] === "m" ? 1e6 : 1));
}
